import React, { useState, useRef, useEffect } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import { SiGoogledrive } from 'react-icons/si';
import { FiEdit2 } from 'react-icons/fi';
import { IoMdClose } from 'react-icons/io';
import {
  useLazyGetGoogleDriveConnectionStatusQuery,
  useUpdateMultipleCandidatesMutation,
} from '../../../features/apis/intergration';
import useCurrentUser from '../../../customHook/useCurrentUser';
import ReactToastify from '../../../customHook/useReactToastify';

const templateOptions = ['Shortlisted', 'Rejected', 'Call for Interview'];

const defaultTemplateBodies = {
  Shortlisted: `Hi ,\n\nWe reviewed your profile for {{ROLE_TITLE}} and would like to schedule an interview.\n\nScreening Questions:\n{{QUESTIONS}}\n\nDocument Requirements:\n{{DOCS}}\n\nPlease reply with your availability (2 slots) and attach/share requested documents.\n\nThanks,\nHR Team`,
  Rejected: `Hi ,\n\nThank you for your interest in {{ROLE_TITLE}}. Unfortunately, we will not be moving forward at this time.\n\nBest of luck,\nHR Team`,
  'Call for Interview': `Hi ,\n\nWe'd love to hop on a call to discuss the {{ROLE_TITLE}} position further.\n\nAre you free this week?\n\nThanks,\nHR Team`,
};
const templateSubjects = {
  Shortlisted: 'Interview Invitation — {{ROLE_TITLE}}',
  Rejected: 'Application Update — {{ROLE_TITLE}}',
  'Call for Interview': 'Interview Call — {{ROLE_TITLE}}',
};
const AvatarStack = ({ candidates }: { candidates: any[] }) => (
  <div className="border border-gray-200 rounded-2xl p-4 flex flex-col items-center relative bg-white w-full shadow-sm">
    <div className="flex -space-x-3 mb-2">
      {candidates.slice(0, 4).map((cand) => {
        const initials = cand.name
          .split(' ')
          .map((n: string) => n[0])
          .slice(0, 2)
          .join('')
          .toUpperCase();

        return (
          <div
            key={cand.id}
            className="w-10 h-10 rounded-full border-2 border-white bg-purple-100 flex items-center justify-center text-purple-600 font-bold shadow-sm"
            title={cand.name}
          >
            {initials}
          </div>
        );
      })}
      <div className="w-10 h-10 rounded-full border-2 border-white bg-purple-50 flex items-center justify-center text-purple-600 cursor-pointer hover:bg-purple-100 transition-colors">
        <FiEdit2 size={14} />
      </div>
    </div>
    <p className="text-[11px] text-gray-400 font-medium tracking-tight">
      {candidates.length} People Selected for The Role
    </p>
  </div>
);

const DriveSyncItem = ({
  title,
  iconColor,
  date,
}: {
  title: string;
  iconColor: string;
  date: string;
}) => (
  <div className="flex items-center gap-3 p-3 border border-gray-100 rounded-2xl hover:bg-gray-50 cursor-pointer transition-all group">
    <SiGoogledrive
      className={`${iconColor} text-2xl group-hover:scale-110 transition-transform`}
    />
    <div className="flex-1 overflow-hidden">
      <p className="text-[13px] font-bold text-gray-700 truncate">{title}</p>
      <p className="text-[9px] text-gray-400 leading-tight">{date}</p>
    </div>
  </div>
);
export default function CandidateReviewModal({
  open,
  onClose,
  candidateArray,
}: {
  open: boolean;
  onClose: () => void;
  candidateArray: any[];
}) {
  const [template, setTemplate] = useState('Shortlisted');
  const [templateBodiesState, setTemplateBodiesState] = useState(
    defaultTemplateBodies
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [updateMultipleCandidates] = useUpdateMultipleCandidatesMutation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isSending, setIsSending] = useState(false);

  const [
    getGoogleDriveConnectionStatus,
    { data: googleDriveConnectionStatusData },
  ] = useLazyGetGoogleDriveConnectionStatusQuery();
  const user = useCurrentUser();
  const candidate = candidateArray[0];

  useEffect(() => {
    if (candidate) {
      setTemplateBodiesState((prev) => {
        const updated = { ...prev };

        const questionsArray = candidate.questions || [];
        const docsArray = candidate.documents || [];
        const questionsSection = questionsArray.length
          ? `Screening Questions:\n${questionsArray.join('\n')}\n\n`
          : '';
        const docsSection = docsArray.length
          ? `Document Requirements:\n${docsArray.join('\n')}\n\n`
          : '';

        Object.keys(updated).forEach((key) => {
          let body =
            defaultTemplateBodies[key as keyof typeof defaultTemplateBodies];
          body = body.replace(
            '{{ROLE_TITLE}}',
            candidate.primaryJob?.name || ''
          );
          body = body.replace('{{QUESTIONS}}', questionsSection);
          body = body.replace('{{DOCS}}', docsSection);
          body = body.replace(/\n{3,}/g, '\n\n');
          updated[key as keyof typeof updated] = body.trim();
        });

        return updated;
      });
    }
  }, [candidate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (user?.companyId) {
      getGoogleDriveConnectionStatus(user.companyId);
    }
  }, [getGoogleDriveConnectionStatus, user?.companyId]);
  const handleSendMail = async () => {
    if (!candidateArray || candidateArray.length === 0) return;

    const subject = templateSubjects[template].replace(
      '{{ROLE_TITLE}}',
      candidate.primaryJob?.name || ''
    );
    const body = templateBodiesState[template];
    const candidateIds = candidateArray.map((cand: any) => cand._id);

    try {
      setIsSending(true); // Start loading
      const data = await updateMultipleCandidates({
        candidateIds,
        status: template,
        subject,
        body,
      }).unwrap();

      ReactToastify({
        success: true,
        message: 'Candidates updated and notified individually',
      });
    } catch (err) {
      console.error('Failed to send email to candidates', err);
      ReactToastify({
        success: false,
        message: 'Failed to send email to candidates',
      });
    } finally {
      setIsSending(false);
      onClose();
    }
  };

  if (!open || candidateArray.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-5xl bg-white rounded-2xl overflow-hidden flex flex-col md:flex-row min-h-[650px] shadow-[#F8F5FF] animate-in fade-in duration-200 py-6">
        <div className="w-1/3 px-4 flex flex-col gap-5 overflow-y-auto mt-6">
          <AvatarStack candidates={candidateArray} />
          <div className="border border-gray-100 rounded-3xl p-6 text-center shadow-sm bg-white">
            <h2 className="text-lg font-bold text-gray-800 leading-tight">
              {candidate.primaryJob?.name || 'Role Not Available'}
            </h2>
            <div className="flex flex-wrap gap-2 justify-center mt-5">
              {candidate.skills?.slice(0, 5).map((s: string) => (
                <span
                  key={s}
                  className="px-3 py-1 bg-[#F5F2FF] text-[#6E3EF5] text-[10px] rounded-full font-bold uppercase tracking-widest"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 max-h-64 overflow-y-auto pr-2 border border-[#EFEFEF] rounded-xl p-4 shadow-sm bg-white hide-scrollbar ">
            {googleDriveConnectionStatusData?.googleSyncHistory?.map(
              (item: any, index: number) => (
                <DriveSyncItem
                  key={item._id || index}
                  title={`Google Drive Sync ${index + 1}`}
                  iconColor="text-blue-500"
                  date={new Date(item.syncedAt).toLocaleString()}
                />
              )
            ) || (
              <p className="text-gray-400 text-sm">
                No Drive sync history available.
              </p>
            )}
          </div>
        </div>
        <div className="flex-1 p-8 bg-white flex flex-col w-2/3">
          <div>
            <button className="px-6 py-2.5 bg-[#6E3EF5] text-white rounded-full text-sm font-bold flex items-center gap-2 hover:bg-[#5a31d6] transition-colors shadow-lg shadow-purple-100">
              {template}
              <span className="w-2 h-2 bg-white rounded-full opacity-60" />
            </button>
          </div>
          <hr className="border-t border-gray-300 my-6" />
          <div className="flex-1 border border-[#EFEFEF] rounded-xl overflow-hidden flex flex-col shadow-sm p-6">
            <div className="px-6 py-4 bg-[#F9F6FF] rounded-lg text-sm text-[#272727]">
              <span className="font-medium">Subject : </span>
              <span className="font-medium ml-1">
                {templateSubjects[template].replace(
                  '{{ROLE_TITLE}}',
                  candidate.primaryJob?.name || ''
                )}
              </span>
            </div>

            <div className="relative my-4" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full text-left text-sm bg-[#F9F6FF] px-6 py-4 font-medium flex justify-between items-center group text-[#272727] rounded-lg"
              >
                <span>
                  Template :{' '}
                  <span className="text-gray-700 font-bold ml-1">
                    {template}
                  </span>
                </span>
                <HiChevronDown
                  className={`text-xl transition-transform duration-200 ${
                    isDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isDropdownOpen && (
                <div className="absolute left-6 top-full mt-1 w-64 bg-white border border-gray-100 rounded-2xl shadow-xl z-10 py-2">
                  {templateOptions.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => {
                        setTemplate(opt);
                        setIsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-purple-50 hover:text-[#6E3EF5] transition-colors font-medium"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex-1 relative bg-[#F9F6FF] rounded-xl">
              <textarea
                value={templateBodiesState[template]}
                onChange={(e) =>
                  setTemplateBodiesState((prev) => ({
                    ...prev,
                    [template]: e.target.value,
                  }))
                }
                className="w-full h-full p-8 text-[14px] text-gray-600 leading-relaxed outline-none resize-none font-medium bg-[#F9F6FF] rounded-xl"
                placeholder="Start typing your email message..."
              />
            </div>

            <div className="py-5 bg-white border-t border-gray-50 flex justify-start">
              <button
                onClick={handleSendMail}
                disabled={isSending}
                className={`px-10 py-3 text-white rounded-lg text-sm font-black tracking-wide transition-all transform active:scale-95 shadow-xl shadow-purple-200 ${
                  isSending
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#B79DFF] to-[#3800D7] hover:bg-[#5a31d6]'
                }`}
              >
                {isSending ? 'Sending...' : 'Send Mail'}
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full text-slate-500 flex items-center justify-center text-lg transition-colors hover:text-red-500"
        >
          <IoMdClose />
        </button>
      </div>
    </div>
  );
}
